import React, { useState, useMemo } from 'react';
import { Search } from 'lucide-react';
import { SectionCard } from '../../components/sections/SectionCard';
import { PanelTable } from '../../components/display/PanelTable';
import { AppStatusBadge } from '@shared/components/ui/status-badge/AppStatusBadge';
import { formatDateOnly } from '@shared/utils/date';
import { getSubscriptionRowActions } from '@shared/lib/actions/subscription-actions.tsx';
import { Input } from '@shared/components/ui/input';
import type { PanelTableColumn } from '../../components/display/PanelTable/types';
import type { Subscription } from '@/features/subscriptions/types/subscriptions/subscription.types';
import type { SubscriptionActionHandlers } from '@shared/lib/actions/subscription-actions.tsx';

interface SubscriptionsSectionProps {
  subscriptions: Subscription[];
  isDetailView?: boolean;
  actionHandlers?: SubscriptionActionHandlers;
  className?: string;
  showFilters?: boolean;
}

const SUBSCRIPTION_STATUS_OPTIONS = ['all', 'active', 'inactive', 'scheduled', 'cancelled'] as const;

const renderStatus = (_: unknown, item: Subscription) => (
  <div className="whitespace-nowrap">
    <AppStatusBadge
      entityType="subscription"
      status={item.status}
      size="sm"
    />
  </div>
);

const renderCreated = (_: unknown, item: Subscription) => (
  <span className="text-sm text-muted-foreground">
    {formatDateOnly(item.created)}
  </span>
);

export const SubscriptionsSection = React.memo<SubscriptionsSectionProps>(({
  subscriptions,
  isDetailView = false,
  actionHandlers,
  className,
  showFilters = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const filteredSubscriptions = useMemo(() => {
    let filtered = subscriptions;

    if (statusFilter !== 'all') {
      filtered = filtered.filter(sub => sub.status === statusFilter);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(sub =>
        sub.customer.toLowerCase().includes(query) ||
        sub.plan.toLowerCase().includes(query) ||
        sub.product.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [subscriptions, statusFilter, searchQuery]);

  const columns: PanelTableColumn<Subscription>[] = [
    {
      key: 'customer',
      label: 'CUSTOMER',
      headerClassName: 'w-[180px]',
      cellClassName: 'font-medium'
    },
    {
      key: 'status',
      label: 'STATUS',
      headerClassName: 'w-[90px]',
      render: renderStatus
    },
    {
      key: 'plan',
      label: 'PLAN',
      headerClassName: 'w-[150px]'
    },
    {
      key: 'product',
      label: 'PRODUCT',
      headerClassName: 'w-[150px]'
    },
    {
      key: 'avgMonthly',
      label: 'AVG. MONTHLY',
      headerClassName: 'w-[130px]',
      cellClassName: 'font-medium'
    },
    {
      key: 'avgYearly',
      label: 'AVG. YEARLY',
      headerClassName: 'w-[130px]',
      cellClassName: 'font-medium'
    },
    {
      key: 'created',
      label: 'CREATED',
      headerClassName: 'w-[120px]',
      render: renderCreated
    }
  ];

  const subscriptionActions = getSubscriptionRowActions(actionHandlers || {});
  const rowActions = () => subscriptionActions;

  return (
    <SectionCard title="Subscriptions" layout="down" className={className}>
      {showFilters && (
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search subscriptions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9"
            />
          </div>
          <div className="flex items-center gap-1">
            {SUBSCRIPTION_STATUS_OPTIONS.map((status) => (
              <button
                key={status}
                type="button"
                onClick={() => setStatusFilter(status)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors capitalize ${
                  statusFilter === status
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>
      )}
      <PanelTable
        data={filteredSubscriptions}
        columns={columns}
        variant={isDetailView ? 'full' : 'panel'}
        emptyMessage="No subscriptions found"
        pageSize={10}
        rowActions={rowActions}
      />
    </SectionCard>
  );
});

SubscriptionsSection.displayName = 'SubscriptionsSection';
